import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    service: 'Ståltag',
    message: '',
    website_url: '' // Honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website_url) {
      // Spam detected, pretend success
      setSubmitStatus({ success: true, message: 'Tak for din henvendelse! Vi vender tilbage hurtigst muligt.' });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSubmitStatus({ success: true, message: 'Mange tak for din henvendelse! Vi kontakter dig senest næste hverdag.' });
        setFormData({
          name: '',
          address: '',
          email: '',
          phone: '',
          service: 'Ståltag',
          message: '',
          website_url: ''
        });
      } else {
        setSubmitStatus({ success: false, message: 'Der opstod en fejl ved afsendelse. Ring venligst direkte til os på (+45) 28 59 43 49.' });
      }
    } catch (err) {
      setSubmitStatus({ success: false, message: 'Netværksfejl. Prøv venligst igen eller ring til os.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative bg-[#181818] text-white pt-16 sm:pt-24 pb-16 px-6 sm:px-12 md:px-20 font-sans">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        
        {/* Left Column: Branding & Info */}
        <div className="lg:col-span-6 space-y-10 pt-4">
          {/* Logo Container */}
          <div className="flex items-center space-x-3">
            <img 
              src="https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/logo.png" 
              alt="T.H Byg logo" 
              className="h-10 w-auto object-contain brightness-0 invert"
            />
          </div>

          {/* Section: Mission Pitch */}
          <div className="space-y-2">
            <h2 className="font-black text-xl uppercase tracking-wider text-white">Din kvalitetsbevidste tømrermester</h2>
            <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed max-w-md">
              Hos T.H Byg kommer du til at møde mester selv! Det er nemlig ham der både regner tilbuddene samt udfører arbejdet.
            </p>
          </div>

          {/* Section: Contact Details */}
          <div className="space-y-1">
            <h3 className="font-black text-sm uppercase tracking-wider text-white mb-2">Kontakt</h3>
            <p className="text-xs sm:text-sm text-zinc-400 font-medium">
              <a href="tel:28594349" className="hover:text-[#d92323] transition-colors">+45 28 59 43 49</a>
            </p>
            <p className="text-xs sm:text-sm text-zinc-400 font-medium">
              <a href="mailto:jacob.t.h.byg@gmail.com" className="hover:text-[#d92323] transition-colors">jacob.t.h.byg@gmail.com</a>
            </p>
            <p className="text-[10px] text-zinc-500 pt-1">CVR: 31819547</p>
          </div>

          {/* Section: Opening Hours */}
          <div className="space-y-1">
            <h3 className="font-black text-sm uppercase tracking-wider text-white mb-2">Åbningstider</h3>
            <p className="text-xs sm:text-sm text-zinc-400">Man-Tors: 07:00 - 16:00</p>
            <p className="text-xs sm:text-sm text-zinc-400">Fredag: 07:00 - 14:30</p>
            <p className="text-xs sm:text-sm text-zinc-400">Weekend: Lukket</p>
          </div>

          {/* Bottom Links */}
          <div className="pt-8 text-[11px] text-zinc-500 flex flex-wrap gap-4 items-center border-t border-zinc-800">
            <span>© {new Date().getFullYear()} T.H BYG</span>
            <span>•</span>
            <a 
              href="https://www.iubenda.com/privacy-policy/23090860" 
              className="iubenda-white iubenda-noiframe iubenda-embed hover:underline hover:text-white" 
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
            <span>•</span>
            <a 
              href="https://www.iubenda.com/privacy-policy/23090860/cookie-policy" 
              className="iubenda-white iubenda-noiframe iubenda-embed hover:underline hover:text-white" 
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
            <span>•</span>
            <Link to="/projekter" className="hover:underline hover:text-white">Sitemap</Link>
          </div>
        </div>

        {/* Right Column: Card Form */}
        <div className="lg:col-span-6 flex justify-center lg:justify-end relative -mt-16 sm:-mt-24 lg:-mt-36 z-20 w-full">
          <div className="w-full bg-white text-zinc-900 rounded-b-2xl shadow-2xl border-t-4 border-[#d92323] p-8 sm:p-10 md:p-12 space-y-6">
            
            {/* Form Header */}
            <div className="text-center space-y-3">
              <h2 className="font-black text-xl sm:text-2xl uppercase tracking-tight text-zinc-900">
                Få et uforpligtende tilbud
              </h2>
              <p className="font-bold text-xs sm:text-sm text-zinc-800 leading-snug">
                Udfyld venligst nedenfor – og så hører du fra os indenfor meget kort tid.
              </p>
              <p className="text-xs text-zinc-500">
                Du er også meget velkommen til at give os et ring eller skrive en mail.
              </p>
            </div>

            {submitStatus && (
              <div className={`p-4 rounded-lg text-xs font-semibold flex items-center gap-3 ${
                submitStatus.success 
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                <i className={`fa-solid ${submitStatus.success ? 'fa-circle-check text-emerald-600 text-lg' : 'fa-triangle-exclamation text-red-600 text-lg'}`} />
                <span>{submitStatus.message}</span>
              </div>
            )}

            {/* Form Elements */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot anti-spam field */}
              <input
                type="text"
                name="website_url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                className="sr-only absolute pointer-events-none"
                tabIndex={-1}
                autoComplete="off"
              />

              <div>
                <input 
                  type="text" 
                  required
                  placeholder="Dit navn*" 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-[#F0F0F0] text-xs sm:text-sm px-4 py-3.5 rounded-t border-b-2 border-zinc-300 focus:border-[#d92323] focus:bg-white focus:outline-none transition-all placeholder:text-zinc-400"
                />
              </div>

              <div>
                <input 
                  type="text" 
                  required
                  placeholder="Hvad er din adresse?*" 
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full bg-[#F0F0F0] text-xs sm:text-sm px-4 py-3.5 rounded-t border-b-2 border-zinc-300 focus:border-[#d92323] focus:bg-white focus:outline-none transition-all placeholder:text-zinc-400"
                />
              </div>

              <div>
                <input 
                  type="email" 
                  required
                  placeholder="Din e-mail*" 
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-[#F0F0F0] text-xs sm:text-sm px-4 py-3.5 rounded-t border-b-2 border-zinc-300 focus:border-[#d92323] focus:bg-white focus:outline-none transition-all placeholder:text-zinc-400"
                />
              </div>

              <div>
                <input 
                  type="tel" 
                  required
                  placeholder="Dit telefonnummer*" 
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-[#F0F0F0] text-xs sm:text-sm px-4 py-3.5 rounded-t border-b-2 border-zinc-300 focus:border-[#d92323] focus:bg-white focus:outline-none transition-all placeholder:text-zinc-400"
                />
              </div>

              <div>
                <textarea 
                  rows={3} 
                  required
                  placeholder="Beskriv opgaven*" 
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-[#F0F0F0] text-xs sm:text-sm p-4 rounded-t border-b-2 border-zinc-300 focus:border-[#d92323] focus:bg-white focus:outline-none resize-none transition-all placeholder:text-zinc-400"
                ></textarea>
              </div>

              <div className="pt-2">
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#d92323] hover:bg-[#b81b1b] text-white font-extrabold text-sm uppercase tracking-wider py-3.5 px-6 rounded-full shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer disabled:opacity-60"
                >
                  {isSubmitting ? 'Sender tilbud...' : 'Indhent Tilbud'}
                </button>
              </div>

              <p className="text-[10px] text-center text-zinc-500 pt-2 font-medium">
                Felter markeret med * skal udfyldes
              </p>
            </form>

          </div>
        </div>

      </div>
    </footer>
  );
}

